/**
 * User Challenge Detail Screen
 * Shows challenge details and allows users to complete it
 */

import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Alert,
} from "react-native";
import { Image } from "expo-image";
import { SafeAreaView } from "react-native-safe-area-context";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { format, formatDistanceToNow, isPast, isFuture, differenceInMinutes } from "date-fns";
import { nl } from "date-fns/locale";
import * as ImagePicker from "expo-image-picker";
import * as FileSystem from "expo-file-system/legacy";
import { decode } from "base64-arraybuffer";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { logChallengeAction } from "@/lib/activity-log";
import { createInAppNotification } from "@/lib/notifications";
import {
  Challenge,
  ChallengeCompletion,
  PhotoChallengeConfig,
  CHALLENGE_ICONS,
  CHALLENGE_LABELS,
  CHALLENGE_COLORS,
} from "@/modules/challenges/types";

/**
 * User Challenge Detail Screen
 */
export default function UserChallengeDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { activeProfile, userTeam } = useAuth();
  
  // Use activeProfile for submissions (could be dependent user)
  const profile = activeProfile;

  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [completion, setCompletion] = useState<ChallengeCompletion | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  /**
   * Fetch challenge and completion status
   */
  const fetchChallenge = useCallback(async () => {
    if (!id || !profile?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch challenge
      const { data: challengeData, error: challengeError } = await supabase
        .from("challenges")
        .select("*")
        .eq("id", id)
        .single();

      if (challengeError) throw challengeError;
      setChallenge(challengeData as Challenge);

      // Fetch user's completion
      let completionQuery = supabase
        .from("challenge_completions")
        .select("*")
        .eq("challenge_id", id);

      if (userTeam?.id) {
        completionQuery = completionQuery.or(
          `completed_by.eq.${profile.id},team_id.eq.${userTeam.id}`
        );
      } else {
        completionQuery = completionQuery.eq("completed_by", profile.id);
      }

      const { data: completionData } = await completionQuery.maybeSingle();
      setCompletion(completionData as ChallengeCompletion | null);
    } catch (err: any) {
      console.error("Error fetching challenge:", err);
      setError(err.message || "Kon uitdaging niet laden");
    } finally {
      setIsLoading(false);
    }
  }, [id, profile?.id, userTeam?.id]);

  useEffect(() => {
    fetchChallenge();
  }, [fetchChallenge]);

  /**
   * Get availability status
   */
  const getAvailabilityStatus = () => {
    if (!challenge) return null;

    const now = new Date();
    const from = challenge.available_from ? new Date(challenge.available_from) : null;
    const until = challenge.available_until ? new Date(challenge.available_until) : null;

    if (from && isFuture(from)) {
      return {
        available: false,
        label: "Nog niet beschikbaar",
        sublabel: `Start ${formatDistanceToNow(from, { addSuffix: true, locale: nl })}`,
        color: colors.warning,
      };
    }

    if (until && isPast(until)) {
      return {
        available: false,
        label: "Verlopen",
        sublabel: `Eindigde ${formatDistanceToNow(until, { addSuffix: true, locale: nl })}`,
        color: colors.error,
      };
    }

    if (until) {
      const minutesLeft = differenceInMinutes(until, now);
      if (minutesLeft <= 60) {
        return {
          available: true,
          label: "Bijna verlopen!",
          sublabel: `Nog ${minutesLeft} minuten`,
          color: colors.error,
        };
      }
      return {
        available: true,
        label: "Beschikbaar",
        sublabel: `Tot ${format(until, "d MMM HH:mm", { locale: nl })}`,
        color: colors.success,
      };
    }

    return {
      available: true,
      label: "Beschikbaar",
      sublabel: "Geen deadline",
      color: colors.success,
    };
  };

  /**
   * Pick image for photo challenge
   */
  const handlePickImage = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") {
      Alert.alert("Toestemming nodig", "We hebben toegang tot je foto's nodig om een foto te uploaden.");
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ["images"],
      allowsEditing: true,
      aspect: [4, 3],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setSelectedImage(result.assets[0].uri);
    }
  };

  /**
   * Take photo for photo challenge
   */
  const handleTakePhoto = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== "granted") {
      Alert.alert("Toestemming nodig", "We hebben toegang tot je camera nodig om een foto te maken.");
      return;
    }

    const result = await ImagePicker.launchCameraAsync({
      allowsEditing: true,
      aspect: [4, 3],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setSelectedImage(result.assets[0].uri);
    }
  };

  /**
   * Submit photo challenge
   */
  const handleSubmitPhoto = async () => {
    if (!selectedImage || !challenge || !profile?.id) return;

    setIsSubmitting(true);

    try {
      // Read image as base64 using FileSystem
      const base64 = await FileSystem.readAsStringAsync(selectedImage, {
        encoding: "base64",
      });

      // Convert base64 to ArrayBuffer using base64-arraybuffer package
      const arrayBuffer = decode(base64);

      // Upload image to Supabase Storage
      const fileName = `${profile.id}/${challenge.id}/${Date.now()}.jpg`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("challenge-submissions")
        .upload(fileName, arrayBuffer, {
          contentType: "image/jpeg",
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("challenge-submissions")
        .getPublicUrl(fileName);

      // Create or update completion record
      const config = challenge.config as PhotoChallengeConfig;
      const submissionData = {
        photo_url: urlData.publicUrl,
      };

      if (isEditing && completion?.id) {
        // Update existing submission
        const { error: updateError } = await supabase
          .from("challenge_completions")
          .update({
            submission: submissionData,
            status: config?.verification_type === "auto" ? "auto_approved" : "pending",
            points_earned: config?.verification_type === "auto" ? challenge.points : 0,
            completed_at: new Date().toISOString(),
          })
          .eq("id", completion.id);

        if (updateError) throw updateError;

        // Log the submission update
        await logChallengeAction(
          profile.id,
          challenge.id,
          challenge.title,
          "submit",
          { action: "update", submission_id: completion.id, photo_url: urlData.publicUrl }
        );

        // Notify familie admins about updated submission
        if (config?.verification_type !== "auto") {
          try {
            const { data: adminRoles } = await supabase
              .from("user_roles")
              .select("user_id")
              .eq("role", "familie_admin")
              .is("revoked_at", null);

            if (adminRoles && adminRoles.length > 0) {
              const submitterName = profile.nickname || profile.full_name || "Een gebruiker";
              
              for (const admin of adminRoles) {
                if (admin.user_id !== profile.id) {
                  await createInAppNotification({
                    userId: admin.user_id,
                    title: "📝 Inzending gewijzigd",
                    body: `${submitterName} heeft de inzending voor "${challenge.title}" aangepast`,
                    type: "challenge_submission_updated",
                    actionUrl: `/(admin)/games/${challenge.id}`,
                    actionData: { challengeId: challenge.id },
                    senderId: profile.id,
                  });
                }
              }
            }
          } catch (notifyError) {
            console.error("Error notifying admins:", notifyError);
          }
        }

        Alert.alert(
          "Inzending gewijzigd!",
          config?.verification_type === "auto"
            ? `Je hebt ${challenge.points} punten verdiend!`
            : "Je gewijzigde inzending wordt beoordeeld door een admin.",
          [
            {
              text: "OK",
              onPress: () => {
                fetchChallenge();
                setSelectedImage(null);
                setIsEditing(false);
              },
            },
          ]
        );
      } else {
        // Create new submission
        // Always include team_id for scoring purposes (even for individual challenges)
        const { error: completionError } = await supabase
          .from("challenge_completions")
          .insert({
            challenge_id: challenge.id,
            completed_by: profile.id,
            team_id: userTeam?.id || null, // Always capture team for scoring
            submission: submissionData,
            status: config?.verification_type === "auto" ? "auto_approved" : "pending",
            points_earned: config?.verification_type === "auto" ? challenge.points : 0,
            completed_at: new Date().toISOString(),
          });

        if (completionError) throw completionError;

        // Log the new submission
        await logChallengeAction(
          profile.id,
          challenge.id,
          challenge.title,
          "submit",
          { action: "create", photo_url: urlData.publicUrl, team_id: userTeam?.id }
        );

        // Notify familie admins about new submission (only for manual verification)
        if (config?.verification_type !== "auto") {
          try {
            // Fetch all active familie admins
            const { data: adminRoles } = await supabase
              .from("user_roles")
              .select("user_id")
              .eq("role", "familie_admin")
              .is("revoked_at", null);

            if (adminRoles && adminRoles.length > 0) {
              const submitterName = profile.nickname || profile.full_name || "Een gebruiker";
              
              // Send notification to each admin
              for (const admin of adminRoles) {
                if (admin.user_id !== profile.id) {
                  await createInAppNotification({
                    userId: admin.user_id,
                    title: "📸 Nieuwe inzending",
                    body: `${submitterName} heeft een inzending gedaan voor "${challenge.title}"`,
                    type: "challenge_submission",
                    actionUrl: `/(admin)/games/${challenge.id}`,
                    actionData: { challengeId: challenge.id },
                    senderId: profile.id,
                  });
                }
              }
            }
          } catch (notifyError) {
            // Don't fail the submission if notification fails
            console.error("Error notifying admins:", notifyError);
          }
        }

        // Notify team members when a team challenge is completed
        if (challenge.target_type === "team" && userTeam?.id) {
          try {
            // Fetch team members
            const { data: teamMembers } = await supabase
              .from("profiles")
              .select("id, full_name, nickname")
              .eq("team_id", userTeam.id);

            if (teamMembers && teamMembers.length > 0) {
              const submitterName = profile.nickname || profile.full_name || "Een teamgenoot";
              
              // Send notification to each team member (except the submitter)
              for (const member of teamMembers) {
                if (member.id !== profile.id) {
                  await createInAppNotification({
                    userId: member.id,
                    title: "🎯 Team uitdaging voltooid!",
                    body: `${submitterName} heeft "${challenge.title}" ingezonden voor jullie team!`,
                    type: "challenge_team_complete",
                    actionUrl: `/(tabs)/games/challenge/${challenge.id}`,
                    actionData: { challengeId: challenge.id },
                    senderId: profile.id,
                    lottieAnimation: "celebration",
                  });
                }
              }
            }
          } catch (notifyError) {
            // Don't fail the submission if notification fails
            console.error("Error notifying team members:", notifyError);
          }
        }

        Alert.alert(
          "Ingezonden!",
          config?.verification_type === "auto"
            ? `Je hebt ${challenge.points} punten verdiend!`
            : "Je inzending wordt beoordeeld door een admin.",
          [
            {
              text: "OK",
              onPress: () => {
                fetchChallenge();
                setSelectedImage(null);
              },
            },
          ]
        );
      }
    } catch (err: any) {
      console.error("Error submitting challenge:", err);
      Alert.alert("Fout", "Kon inzending niet versturen. Probeer het opnieuw.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle delete submission with confirmation
   */
  const handleDeleteSubmission = () => {
    if (!completion?.id || !challenge) return;

    Alert.alert(
      "Inzending verwijderen",
      "Weet je zeker dat je deze inzending wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
      [
        {
          text: "Annuleren",
          style: "cancel",
        },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            setIsDeleting(true);
            try {
              // Delete the completion record
              console.log("Deleting submission with ID:", completion.id);
              const { error: deleteError, count } = await supabase
                .from("challenge_completions")
                .delete()
                .eq("id", completion.id)
                .select();

              console.log("Delete result - error:", deleteError, "count:", count);
              if (deleteError) throw deleteError;

              // Log the deletion
              await logChallengeAction(
                profile!.id,
                challenge.id,
                challenge.title,
                "submit",
                { action: "delete", submission_id: completion.id }
              );

              // Clear state immediately
              setCompletion(null);
              setIsEditing(false);
              setSelectedImage(null);

              Alert.alert(
                "Verwijderd",
                "Je inzending is verwijderd. Je kunt opnieuw indienen.",
                [
                  {
                    text: "OK",
                    onPress: () => {
                      fetchChallenge();
                    },
                  },
                ]
              );
            } catch (err: any) {
              console.error("Error deleting submission:", err);
              Alert.alert("Fout", "Kon inzending niet verwijderen. Probeer het opnieuw.");
            } finally {
              setIsDeleting(false);
            }
          },
        },
      ]
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={["top", "left", "right"]}>
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </SafeAreaView>
    );
  }

  // Error state
  if (error || !challenge) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={["top", "left", "right"]}>
        <View className="flex-1 items-center justify-center p-6">
          <Ionicons name="warning" size={48} color={colors.error} />
          <Text style={{ color: colors.text }} className="text-lg font-semibold mt-4 text-center">
            {error || "Uitdaging niet gevonden"}
          </Text>
          <TouchableOpacity
            onPress={() => router.back()}
            className="mt-6 px-6 py-3 rounded-xl"
            style={{ backgroundColor: colors.primary }}
          >
            <Text className="text-white font-semibold">Terug</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const challengeColor = CHALLENGE_COLORS[challenge.challenge_type] || colors.primary;
  const config = challenge.config as PhotoChallengeConfig;
  const availabilityStatus = getAvailabilityStatus();
  const isCompleted = completion?.status === "approved" || completion?.status === "auto_approved";
  const isPending = completion?.status === "pending";
  const isRejected = completion?.status === "rejected";
  const canSubmit = availabilityStatus?.available && !isCompleted && (!isPending || isEditing);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={["top"]}>
      {/* Header */}
      <View
        className="flex-row items-center px-4 py-3 border-b"
        style={{ borderColor: colors.border }}
      >
        <TouchableOpacity
          onPress={() => router.back()}
          className="p-2 -ml-2"
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text
          style={{ color: colors.text }}
          className="text-lg font-bold flex-1 text-center mr-8"
          numberOfLines={1}
        >
          {CHALLENGE_LABELS[challenge.challenge_type]}
        </Text>
      </View>

      <ScrollView className="flex-1">
        {/* Hero Section */}
        <View
          className="mx-4 mt-4 rounded-2xl p-6"
          style={{ backgroundColor: challengeColor + "15" }}
        >
          <View className="flex-row items-start">
            <View
              className="w-16 h-16 rounded-2xl items-center justify-center"
              style={{ backgroundColor: challengeColor + "30" }}
            >
              <Ionicons
                name={CHALLENGE_ICONS[challenge.challenge_type] as any}
                size={32}
                color={challengeColor}
              />
            </View>

            <View className="flex-1 ml-4">
              <Text className="text-xl font-bold" style={{ color: colors.text }}>
                {challenge.title}
              </Text>

              {/* Status badges */}
              <View className="flex-row flex-wrap mt-2">
                {isCompleted && (
                  <View
                    className="px-3 py-1 rounded-full mr-2 mb-1 flex-row items-center"
                    style={{ backgroundColor: colors.success }}
                  >
                    <Ionicons name="checkmark-circle" size={14} color="#FFFFFF" />
                    <Text className="text-white font-medium ml-1 text-sm">Voltooid</Text>
                  </View>
                )}
                {isPending && (
                  <View
                    className="px-3 py-1 rounded-full mr-2 mb-1 flex-row items-center"
                    style={{ backgroundColor: "#F59E0B" }}
                  >
                    <Ionicons name="time" size={14} color="#FFFFFF" />
                    <Text className="text-white font-medium ml-1 text-sm">In afwachting</Text>
                  </View>
                )}
                {isRejected && (
                  <View
                    className="px-3 py-1 rounded-full mr-2 mb-1 flex-row items-center"
                    style={{ backgroundColor: colors.error }}
                  >
                    <Ionicons name="close-circle" size={14} color="#FFFFFF" />
                    <Text className="text-white font-medium ml-1 text-sm">Afgekeurd</Text>
                  </View>
                )}
                {challenge.target_type === "team" && (
                  <View
                    className="px-3 py-1 rounded-full mr-2 mb-1 flex-row items-center"
                    style={{ backgroundColor: colors.surface }}
                  >
                    <Ionicons name="people" size={14} color={colors.textSecondary} />
                    <Text style={{ color: colors.textSecondary }} className="ml-1 text-sm">
                      Team
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </View>

          {/* Points */}
          {challenge.has_points && (
            <View
              className="mt-4 p-3 rounded-xl flex-row items-center justify-center"
              style={{ backgroundColor: "#F59E0B20" }}
            >
              <Ionicons name="star" size={24} color="#F59E0B" />
              <Text className="text-2xl font-bold ml-2" style={{ color: "#F59E0B" }}>
                {challenge.points}
              </Text>
              <Text className="ml-1" style={{ color: "#F59E0B" }}>
                punten
              </Text>
              {isCompleted && completion?.points_earned ? (
                <Text className="ml-2" style={{ color: colors.success }}>
                  ✓ Verdiend!
                </Text>
              ) : null}
            </View>
          )}
        </View>

        {/* Availability Status */}
        {availabilityStatus && (
          <View
            className="mx-4 mt-4 p-4 rounded-xl flex-row items-center"
            style={{ backgroundColor: availabilityStatus.color + "15" }}
          >
            <Ionicons
              name={availabilityStatus.available ? "checkmark-circle" : "time"}
              size={24}
              color={availabilityStatus.color}
            />
            <View className="ml-3">
              <Text className="font-semibold" style={{ color: availabilityStatus.color }}>
                {availabilityStatus.label}
              </Text>
              <Text className="text-sm" style={{ color: colors.textSecondary }}>
                {availabilityStatus.sublabel}
              </Text>
            </View>
          </View>
        )}

        {/* Description */}
        {challenge.description && (
          <View className="mx-4 mt-4">
            <Text className="font-semibold mb-2" style={{ color: colors.text }}>
              Beschrijving
            </Text>
            <View className="p-4 rounded-xl" style={{ backgroundColor: colors.surface }}>
              <Text style={{ color: colors.text }}>{challenge.description}</Text>
            </View>
          </View>
        )}

        {/* Photo Challenge Requirements */}
        {challenge.challenge_type === "photo" && config && (
          <View className="mx-4 mt-4">
            <Text className="font-semibold mb-2" style={{ color: colors.text }}>
              Vereisten
            </Text>
            <View className="p-4 rounded-xl" style={{ backgroundColor: colors.surface }}>
              {config.requirements && config.requirements.length > 0 ? (
                config.requirements.map((req, index) => (
                  <View key={index} className="flex-row items-center mb-2 last:mb-0">
                    <Ionicons name="checkmark-circle" size={16} color={colors.primary} />
                    <Text className="ml-2" style={{ color: colors.text }}>
                      {req}
                    </Text>
                  </View>
                ))
              ) : (
                <Text style={{ color: colors.textSecondary }}>
                  Maak een foto die past bij de beschrijving
                </Text>
              )}

              {config.min_people > 1 && (
                <View className="flex-row items-center mt-2 pt-2 border-t" style={{ borderColor: colors.border }}>
                  <Ionicons name="people" size={16} color={colors.textSecondary} />
                  <Text className="ml-2" style={{ color: colors.textSecondary }}>
                    Minimaal {config.min_people} personen op de foto
                  </Text>
                </View>
              )}

              {config.require_location && (
                <View className="flex-row items-center mt-2">
                  <Ionicons name="location" size={16} color={colors.textSecondary} />
                  <Text className="ml-2" style={{ color: colors.textSecondary }}>
                    Locatie wordt opgeslagen
                  </Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Rejection reason */}
        {isRejected && completion?.rejection_reason && (
          <View className="mx-4 mt-4">
            <View
              className="p-4 rounded-xl"
              style={{ backgroundColor: colors.error + "15" }}
            >
              <Text className="font-semibold" style={{ color: colors.error }}>
                Reden voor afkeuring
              </Text>
              <Text className="mt-1" style={{ color: colors.text }}>
                {completion.rejection_reason}
              </Text>
              <Text className="mt-2 text-sm" style={{ color: colors.textSecondary }}>
                Je kunt het opnieuw proberen hieronder.
              </Text>
            </View>
          </View>
        )}

        {/* Photo Submission */}
        {challenge.challenge_type === "photo" && canSubmit && (
          <View className="mx-4 mt-6">
            <Text className="font-semibold mb-3" style={{ color: colors.text }}>
              {isEditing ? "Nieuwe foto uploaden" : "Foto uploaden"}
            </Text>

            {selectedImage ? (
              <View>
                <Image
                  source={{ uri: selectedImage }}
                  style={{ width: "100%", height: 256, borderRadius: 12 }}
                  contentFit="cover"
                />
                <TouchableOpacity
                  onPress={() => setSelectedImage(null)}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full items-center justify-center"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                  <Ionicons name="close" size={20} color="#FFFFFF" />
                </TouchableOpacity>
              </View>
            ) : (
              <View className="flex-row gap-3">
                <TouchableOpacity
                  onPress={handleTakePhoto}
                  className="flex-1 py-6 rounded-xl items-center"
                  style={{ backgroundColor: challengeColor + "15" }}
                >
                  <Ionicons name="camera" size={32} color={challengeColor} />
                  <Text className="mt-2 font-medium" style={{ color: challengeColor }}>
                    Camera
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={handlePickImage}
                  className="flex-1 py-6 rounded-xl items-center"
                  style={{ backgroundColor: colors.surface }}
                >
                  <Ionicons name="images" size={32} color={colors.textSecondary} />
                  <Text className="mt-2 font-medium" style={{ color: colors.textSecondary }}>
                    Galerij
                  </Text>
                </TouchableOpacity>
              </View>
            )}

            {selectedImage && (
              <TouchableOpacity
                onPress={handleSubmitPhoto}
                disabled={isSubmitting}
                className="mt-4 py-4 rounded-xl items-center flex-row justify-center"
                style={{
                  backgroundColor: isSubmitting ? colors.textTertiary : colors.primary,
                }}
              >
                {isSubmitting ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <>
                    <Ionicons name={isEditing ? "refresh" : "send"} size={20} color="#FFFFFF" />
                    <Text className="text-white font-bold ml-2">
                      {isEditing ? "Wijzigen" : "Versturen"}
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Completed submission preview */}
        {(isCompleted || isPending) && completion?.submission && (
          <View className="mx-4 mt-6">
            <Text className="font-semibold mb-3" style={{ color: colors.text }}>
              Jouw inzending
            </Text>
            {(completion.submission as any).photo_url && (
              <Image
                source={{ uri: (completion.submission as any).photo_url }}
                style={{ width: "100%", height: 256, borderRadius: 12 }}
                contentFit="cover"
              />
            )}
            <Text className="mt-2 text-sm text-center" style={{ color: colors.textSecondary }}>
              Ingezonden {formatDistanceToNow(new Date(completion.completed_at), { addSuffix: true, locale: nl })}
            </Text>

            {/* Change submission button - only for pending */}
            {isPending && availabilityStatus?.available && !isEditing && (
              <TouchableOpacity
                onPress={() => setIsEditing(true)}
                className="mt-4 py-3 px-6 rounded-xl flex-row items-center justify-center"
                style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
              >
                <Ionicons name="create-outline" size={20} color={colors.primary} />
                <Text className="ml-2 font-semibold" style={{ color: colors.primary }}>
                  Inzending wijzigen
                </Text>
              </TouchableOpacity>
            )}

            {/* Cancel editing button */}
            {isEditing && (
              <TouchableOpacity
                onPress={() => {
                  setIsEditing(false);
                  setSelectedImage(null);
                }}
                className="mt-4 py-3 px-6 rounded-xl flex-row items-center justify-center"
                style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
              >
                <Ionicons name="close" size={20} color={colors.textSecondary} />
                <Text className="ml-2 font-semibold" style={{ color: colors.textSecondary }}>
                  Annuleren
                </Text>
              </TouchableOpacity>
            )}

            {/* Delete submission button - only for pending */}
            {isPending && !isEditing && (
              <TouchableOpacity
                onPress={handleDeleteSubmission}
                disabled={isDeleting}
                className="mt-4 py-3 px-6 rounded-xl flex-row items-center justify-center"
                style={{ backgroundColor: colors.error + "20", borderWidth: 1, borderColor: colors.error }}
              >
                {isDeleting ? (
                  <ActivityIndicator size="small" color={colors.error} />
                ) : (
                  <>
                    <Ionicons name="trash-outline" size={20} color={colors.error} />
                    <Text className="ml-2 font-semibold" style={{ color: colors.error }}>
                      Inzending verwijderen
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            )}
          </View>
        )}

        <View className="h-8" />
      </ScrollView>
    </SafeAreaView>
  );
}

